/**
 * The timeclock, from the terminal somebody walks up to.
 *
 * Four endpoints existed — clock in, clock out, start a break, end one — and
 * nothing anywhere called three of them. So the table that feeds the rota's
 * "who is on", the compliance report, and labour cost against sales was
 * written to by nobody, and the only reason anyone noticed was a punch left
 * open overnight that had to be closed from the database.
 *
 * WHY IT IS SAID IN THE HEADER
 *
 * Clocking in is the first thing somebody does on arriving and the last thing
 * they do before leaving, with a coat still on in both cases. Three taps into
 * a settings sheet is where features go to be forgotten — and a shift nobody
 * clocked is a shift nobody is paid for, which is a complaint rather than a
 * bug report. The drawer sits in the same header for the same reason.
 *
 * WHY IT IS ALWAYS YOU
 *
 * A waiter has `staff.timeclock.self` and not `staff.employee.read`, so a
 * screen that offered a list of names to pick from could not load the list.
 * That is the right permission split: clocking somebody *else* in is a
 * manager's act, and it wants an audit trail this screen does not have. So the
 * clock acts on whoever is signed in, and `/v1/staff/me` is what says who that
 * is and where they stand.
 *
 * WHY A BREAK IS NOT A CLOCK-OUT
 *
 * Clocking out and back in makes two shifts, which reads on a timesheet as
 * somebody who left at three and came back at half past — and quietly loses
 * the break that compliance is measured against. The break buttons exist so
 * the honest thing is also the easy one.
 */

import {
  displayLocale,
  useClockIn,
  useClockOut,
  useEndBreak,
  useMyShift,
  useStartBreak,
  type MyShift,
} from '@restaurant/api-client';
import { ActionError, Button, Empty, Loading, Sheet } from '@restaurant/ui';

import { elapsed, useTicking } from './shift-status';

/** Everything the clock can do, in one sheet. */
export function ClockSheet({
  locationId,
  onDismiss,
}: {
  locationId: string | undefined;
  onDismiss: () => void;
}) {
  const shift = useMyShift();
  const clockIn = useClockIn();
  const clockOut = useClockOut();
  const startBreak = useStartBreak();
  const endBreak = useEndBreak();
  const now = useTicking(shift.data?.on_shift ?? false);

  const busy =
    clockIn.isPending || clockOut.isPending || startBreak.isPending || endBreak.isPending;

  return (
    <Sheet title="Your shift" onDismiss={onDismiss}>
      {shift.isPending && <Loading label="Checking the clock" />}

      {shift.data?.employee_id === null && (
        <Empty
          title="This login is not on the payroll"
          description="Somebody with access to the back office can add you as an employee, and this login will then be able to clock in."
        />
      )}

      {shift.data?.employee_id && (
        <>
          <p className="ck__who">{shift.data.name}</p>
          <p className="ck__state">{describe(shift.data, now)}</p>

          {shift.data.on_shift ? (
            <div className="ck__acts">
              {shift.data.on_break ? (
                <Button
                  variant="primary"
                  size="lg"
                  block
                  loading={endBreak.isPending}
                  disabled={busy}
                  onClick={() =>
                    endBreak.mutate({ employeeId: shift.data.employee_id as string })
                  }
                >
                  Back from break
                </Button>
              ) : (
                <Button
                  size="lg"
                  block
                  loading={startBreak.isPending}
                  disabled={busy}
                  onClick={() =>
                    startBreak.mutate({ employeeId: shift.data.employee_id as string })
                  }
                >
                  Take a break
                </Button>
              )}

              {/* Last, and never the primary: the common reason to open this
                  sheet mid-shift is a break, and clocking out by mistake is a
                  shift somebody has to have corrected by hand. */}
              <Button
                variant="secondary"
                size="lg"
                block
                loading={clockOut.isPending}
                disabled={busy || !locationId}
                onClick={() =>
                  clockOut.mutate({
                    employeeId: shift.data.employee_id as string,
                    locationId: locationId as string,
                  })
                }
              >
                Clock out
              </Button>
            </div>
          ) : (
            <div className="ck__acts">
              <Button
                variant="primary"
                size="lg"
                block
                loading={clockIn.isPending}
                disabled={busy || !locationId}
                onClick={() =>
                  clockIn.mutate({
                    employeeId: shift.data.employee_id as string,
                    locationId: locationId as string,
                  })
                }
              >
                Clock in
              </Button>
            </div>
          )}

          {/* A site on a rule pack may let somebody in with a warning — too
              little rest since yesterday, a young worker past their latest
              finish. They are clocked in; this says what the rules said, so
              it is not a surprise on the compliance report later. A rule set
              to refuse arrives as the error below instead. */}
          {clockIn.data?.warnings && clockIn.data.warnings.length > 0 && (
            <div className="ck__warn" role="status">
              Clocked in. This site&apos;s rules say:
              <ul>
                {clockIn.data.warnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </div>
          )}
          <ActionError error={clockIn.error} action="Clocking in" />
          <ActionError error={clockOut.error} action="Clocking out" />
          <ActionError error={startBreak.error} action="Starting a break" />
          <ActionError error={endBreak.error} action="Ending a break" />
        </>
      )}
    </Sheet>
  );
}

/** What the clock says, in a sentence somebody reads rather than parses. */
function describe(shift: MyShift, now: number): string {
  if (shift.on_break && shift.break_started_at) {
    return `On a break since ${timeOf(shift.break_started_at)}, ${elapsed(shift.break_started_at, now)} so far.`;
  }
  if (shift.on_shift && shift.started_at) {
    return `On since ${timeOf(shift.started_at)}, ${elapsed(shift.started_at, now)} so far.`;
  }
  return 'Not clocked in.';
}

function timeOf(iso: string): string {
  return new Date(iso).toLocaleTimeString(displayLocale(), {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** How long, as somebody would say it. */
